import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Environment } from '../classes/environment';
import { UserProfileImageService } from './user-profile-image.service';

interface FullCacheEntry {
	url: string;
	etag?: string;
	expiresAt: number;
}

interface FullFetchResult {
	entry: FullCacheEntry | null;
	reuseExisting: boolean;
}

@Injectable({ providedIn: 'root' })
export class AvatarsService {

	private readonly fullCacheTtlMs = 30 * 60 * 1000;
	private readonly fullCacheLimit = 20;

	private miniCache = new Map<string, string | null>();
	private miniSubjects = new Map<string, ReplaySubject<string | null>>();

	private fullCache = new Map<string, FullCacheEntry>();
	private fullInFlight = new Map<string, Observable<string | null>>();

	constructor(
		private readonly userProfileImageService: UserProfileImageService,
		private readonly http: HttpClient,
		@Inject('environment') private readonly environment: Environment
	) {}

	mini$(ebeId: string): Observable<string | null> {
		if (!ebeId) return of(null);
		const subj = this.getOrCreateMiniSubject(ebeId);
		if (this.miniCache.has(ebeId)) {
			subj.next(this.miniCache.get(ebeId) ?? null);
		}
		return subj.asObservable();
	}

	ensureMinis(ebeIds: string[]): Observable<Map<string, string | null>> {
		const uniqueIds = Array.from(new Set(ebeIds.filter(id => !!id)));
		if (!uniqueIds.length) return of(new Map());

		const missing = uniqueIds.filter(id => !this.miniCache.has(id));
		if (!missing.length) return of(this.copyMiniCache(uniqueIds));

		return this.userProfileImageService.listMini(missing).pipe(
			tap(resultMap => {
				for (const id of missing) {
					const dataUrl = resultMap.get(id) ?? null;
					this.miniCache.set(id, dataUrl);
					this.getOrCreateMiniSubject(id).next(dataUrl);
				}
			}),
			map(() => this.copyMiniCache(uniqueIds)),
			catchError(() => of(this.copyMiniCache(uniqueIds)))
		);
	}

	full$(ebeId: string): Observable<string | null> {
		if (!ebeId) return of(null);
		const cached = this.fullCache.get(ebeId);
		const now = Date.now();
		if (cached && cached.expiresAt > now) {
			// refresh order
			this.fullCache.delete(ebeId);
			this.fullCache.set(ebeId, cached);
			return of(cached.url);
		}

		const inFlight = this.fullInFlight.get(ebeId);
		if (inFlight) return inFlight;

		const request$ = this.fetchFullImage(ebeId, cached).pipe(
			tap(result => {
				if (!result) return;
				if (result.entry) {
					this.storeFullEntry(ebeId, result.entry);
				}
				else {
					this.removeFullEntry(ebeId);
				}
			}),
			map(result => result?.entry?.url ?? null),
			finalize(() => this.fullInFlight.delete(ebeId)),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.fullInFlight.set(ebeId, request$);
		return request$;
	}

	invalidate(ebeIds?: string | string[]): void {
		const ids = !ebeIds
			? Array.from(new Set([
				...this.miniCache.keys(),
				...this.fullCache.keys()
			]))
			: (Array.isArray(ebeIds) ? ebeIds : [ebeIds]);

		for (const id of ids) {
			if (!id) continue;

			if (this.miniCache.has(id)) {
				this.miniCache.delete(id);
				this.getOrCreateMiniSubject(id).next(null);
			}

			this.removeFullEntry(id);
		}
	}

	private getOrCreateMiniSubject(ebeId: string): ReplaySubject<string | null> {
		let subj = this.miniSubjects.get(ebeId);
		if (!subj) {
			subj = new ReplaySubject<string | null>(1);
			this.miniSubjects.set(ebeId, subj);
			subj.next(null);
		}
		return subj;
	}

	private copyMiniCache(ids: string[]): Map<string, string | null> {
		const out = new Map<string, string | null>();
		for (const id of ids) {
			out.set(id, this.miniCache.get(id) ?? null);
		}
		return out;
	}

	private fetchFullImage(ebeId: string, cached?: FullCacheEntry | null): Observable<FullFetchResult | null> {
		let headers = new HttpHeaders();
		if (cached?.etag) headers = headers.set('If-None-Match', cached.etag);

		return this.http.get(`${this.environment.backendUrl}benutzer/profilbild/${ebeId}`, {
			responseType: 'blob',
			observe: 'response',
			withCredentials: true,
			headers
		}).pipe(
			map((response: HttpResponse<Blob>) => {
				if (response.status === 304 && cached) {
					return {
						entry: { ...cached, expiresAt: Date.now() + this.fullCacheTtlMs },
						reuseExisting: true
					} as FullFetchResult;
				}

				const blob = response.body;
				if (!blob || response.status === 204) {
					return { entry: null, reuseExisting: false } as FullFetchResult;
				}

				const url = URL.createObjectURL(blob);
				const etag = response.headers.get('ETag') ?? undefined;
				if (cached?.url && cached.url !== url) {
					URL.revokeObjectURL(cached.url);
				}

				return {
					entry: {
						url,
						etag,
						expiresAt: Date.now() + this.fullCacheTtlMs
					},
					reuseExisting: false
				} as FullFetchResult;
			}),
			catchError(() => {
				if (cached) {
					return of({
						entry: { ...cached, expiresAt: Date.now() + this.fullCacheTtlMs },
						reuseExisting: true
					} as FullFetchResult);
				}
				return of(null);
			})
		);
	}

	private storeFullEntry(ebeId: string, entry: FullCacheEntry): void {
		const existing = this.fullCache.get(ebeId);
		if (existing && existing.url !== entry.url) {
			URL.revokeObjectURL(existing.url);
		}

		this.fullCache.delete(ebeId);
		this.fullCache.set(ebeId, entry);
		this.trimFullCache();
	}

	private removeFullEntry(ebeId: string): void {
		const existing = this.fullCache.get(ebeId);
		if (existing) {
			URL.revokeObjectURL(existing.url);
			this.fullCache.delete(ebeId);
		}
	}

	private trimFullCache(): void {
		while (this.fullCache.size > this.fullCacheLimit) {
			const oldestKey = this.fullCache.keys().next().value;
			if (!oldestKey) break;
			const entry = this.fullCache.get(oldestKey);
			if (entry) {
				URL.revokeObjectURL(entry.url);
			}
			this.fullCache.delete(oldestKey);
		}
	}
}
